import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';

// Free-form location finder for the annotation map: city, district or a
// specific street. Reuses the existing Neshan-first /api/address proxy
// (Neshan -> OpenCage -> Nominatim) so behaviour matches the coverage tool.
//
// Visually this is one more instrument on the satellite flight deck: the same
// dark-glass shell as the draw/selection HUD, with two deliberate accents that
// make it feel alive without breaking the cockpit language - a teal focus halo
// and a thin "scanning" sweep along the field while a lookup is in flight.

// 2, not 3: several Iranian cities are two letters (قم, بم) and the /api/address
// proxy resolves them fine - a 3-char floor silently blocked those searches.
const MIN_CHARS = 2;

function normalizeResults(data) {
  if (Array.isArray(data?.results) && data.results.length > 0) return data.results;
  if (data?.location?.lat != null && data?.location?.lng != null) {
    return [{ lat: data.location.lat, lng: data.location.lng, address: data.address }];
  }
  return [];
}

export default function LocationSearch({ onPick }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState(false);

  const debounced = useDebounce(query, 350);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  // Guards a result just chosen so the debounced effect does not immediately
  // re-query the address we just wrote back into the field.
  const pickedRef = useRef(false);

  useEffect(() => {
    if (pickedRef.current) { pickedRef.current = false; return; }
    const q = debounced.trim();
    if (q.length < MIN_CHARS) {
      setResults([]); setOpen(false); setLoading(false); setError(false);
      return;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true); setError(false);
    fetch(`/api/address?search_text=${encodeURIComponent(q)}`, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        const list = normalizeResults(data);
        setResults(list);
        setActive(0);
        setOpen(true);
        setLoading(false);
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setResults([]); setError(true); setOpen(true); setLoading(false);
      });
    return () => ac.abort();
  }, [debounced]);

  const choose = useCallback((r) => {
    if (!r) return;
    pickedRef.current = true;
    setQuery(r.address || '');
    setResults([]);
    setOpen(false);
    setError(false);
    inputRef.current?.blur();
    onPick({ lat: Number(r.lat), lng: Number(r.lng), address: r.address });
  }, [onPick]);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    setQuery(''); setResults([]); setOpen(false); setError(false); setLoading(false);
    inputRef.current?.focus();
  }, []);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { e.preventDefault(); clear(); return; }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setActive((i) => (i < results.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setActive((i) => (i > 0 ? i - 1 : results.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault(); choose(results[active]);
    }
  }, [open, results, active, choose, clear]);

  useEffect(() => {
    const onDocDown = (ev) => {
      if (boxRef.current && !boxRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const showPanel = open && (loading || error || results.length > 0);

  return (
    <div
      ref={boxRef}
      style={{
        // Placement is owned by the parent top control strip; this component
        // just fills the slot it is given and floats its own results panel.
        position: 'relative',
        width: '100%',
        zIndex: 6,
        fontFamily: "'Vazirmatn', sans-serif",
        direction: 'rtl',
      }}
    >
      <style>{
        '@keyframes fttxScan{0%{transform:translateX(110%)}100%{transform:translateX(-110%)}}'
        + '@keyframes fttxRes{0%{opacity:0;transform:translateY(-4px)}100%{opacity:1;transform:translateY(0)}}'
      }</style>

      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px 8px 10px',
          overflow: 'hidden',
          background:
            'linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,41,59,0.92) 100%)',
          borderRadius: showPanel ? '13px 13px 0 0' : 13,
          border: '1px solid rgba(148,163,184,0.22)',
          boxShadow: focused
            ? '0 0 0 3px rgba(45,212,191,0.25), 0 10px 30px rgba(2,6,23,0.55), 0 1px 3px rgba(0,0,0,0.45)'
            : '0 1px 0 rgba(255,255,255,0.07) inset, 0 8px 24px rgba(2,6,23,0.5), 0 1px 3px rgba(0,0,0,0.45)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          transition: 'box-shadow 160ms ease, border-radius 160ms ease',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            flexShrink: 0,
            color: '#5eead4',
            background: 'rgba(45,212,191,0.14)',
            border: '1px solid rgba(45,212,191,0.3)',
            borderRadius: 7,
          }}
        >
          <Search size={15} strokeWidth={2.4} />
        </span>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => { setFocused(true); if (results.length || error) setOpen(true); }}
          onBlur={() => setFocused(false)}
          placeholder="جستجوی شهر، منطقه یا خیابان…"
          aria-label="جستجوی مکان"
          autoComplete="off"
          spellCheck="false"
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: '#e2e8f0',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13.5,
            fontWeight: 500,
            lineHeight: 1.6,
            padding: 0,
          }}
        />

        {query && (
          <button
            type="button"
            onClick={clear}
            aria-label="پاک کردن"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              flexShrink: 0,
              padding: 0,
              color: '#cbd5e1',
              background: 'rgba(51,65,85,0.6)',
              border: '1px solid rgba(148,163,184,0.28)',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'background 140ms ease, color 140ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(71,85,105,0.8)'; e.currentTarget.style.color = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(51,65,85,0.6)'; e.currentTarget.style.color = '#cbd5e1'; }}
          >
            <X size={13} strokeWidth={2.6} />
          </button>
        )}

        {/* Scanning sweep - only while a lookup is in flight. */}
        {loading && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 2,
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(90deg, transparent 0%, #2dd4bf 45%, #5eead4 55%, transparent 100%)',
                animation: 'fttxScan 1s linear infinite',
              }}
            />
          </span>
        )}
      </div>

      {showPanel && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            top: '100%',
            zIndex: 30,
            maxHeight: 320,
            overflowY: 'auto',
            background:
              'linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(30,41,59,0.95) 100%)',
            border: '1px solid rgba(148,163,184,0.22)',
            borderTop: 'none',
            borderRadius: '0 0 13px 13px',
            boxShadow: '0 14px 34px rgba(2,6,23,0.6), 0 1px 3px rgba(0,0,0,0.45)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
        >
          {error && (
            <div style={{ padding: '14px 16px', fontSize: 12.5, color: '#fca5a5', fontWeight: 500 }}>
              جستجو ناموفق بود. دوباره تلاش کنید.
            </div>
          )}

          {!error && !loading && results.length === 0 && (
            <div style={{ padding: '14px 16px', fontSize: 12.5, color: '#94a3b8', fontWeight: 500 }}>
              نتیجه‌ای یافت نشد
            </div>
          )}

          {!error && results.map((r, i) => {
            const isActive = i === active;
            return (
              <button
                key={`${r.lat},${r.lng},${i}`}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => choose(r)}
                onMouseEnter={() => setActive(i)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  width: '100%',
                  textAlign: 'right',
                  padding: '10px 14px',
                  border: 'none',
                  borderInlineStart: isActive
                    ? '3px solid #2dd4bf'
                    : '3px solid transparent',
                  background: isActive ? 'rgba(45,212,191,0.12)' : 'transparent',
                  color: isActive ? '#f0fdfa' : '#cbd5e1',
                  fontFamily: "'Vazirmatn', sans-serif",
                  fontSize: 12.5,
                  fontWeight: isActive ? 600 : 500,
                  lineHeight: 1.65,
                  cursor: 'pointer',
                  transition: 'background 120ms ease, color 120ms ease',
                  animation: `fttxRes 220ms ease ${Math.min(i, 8) * 28}ms both`,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    color: isActive ? '#5eead4' : '#64748b',
                    background: isActive ? 'rgba(45,212,191,0.16)' : 'rgba(51,65,85,0.5)',
                    border: `1px solid ${isActive ? 'rgba(45,212,191,0.34)' : 'rgba(148,163,184,0.22)'}`,
                    borderRadius: 6,
                    transition: 'all 120ms ease',
                  }}
                >
                  <MapPin size={13} strokeWidth={2.4} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>{r.address || 'مکان'}</span>
              </button>
            );
          })}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '8px 14px',
              borderTop: '1px solid rgba(148,163,184,0.16)',
              fontSize: 10.5,
              color: '#64748b',
              fontWeight: 500,
            }}
          >
            <span>↑↓ حرکت · Enter انتخاب · Esc بستن</span>
            <span style={{ letterSpacing: '0.04em' }}>NESHAN</span>
          </div>
        </div>
      )}
    </div>
  );
}
